import React, { useEffect, useState } from 'react'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, Container, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import LifeLogo from './LifeLogo'
import { useNavigate, useLocation } from 'react-router-dom'

/**
 * Page de retour du flux Open Banking.
 *
 * Migration Tink → Bridge (API003 v2 / LAP-727) : après l'authentification
 * bancaire dans le widget Bridge Connect, l'utilisateur est redirigé ici.
 * Contrairement à l'ancien flux Tink, il n'y a plus de code OAuth à échanger
 * ni de compte à sélectionner manuellement : les comptes et transactions sont
 * importés côté serveur via le webhook Bridge (item.refreshed).
 *
 * Cette page se contente donc de confirmer le retour puis de renvoyer vers
 * « Mes Comptes », où les comptes apparaissent une fois le webhook traité.
 */
export default function OpenBank() {
  const navigate = useNavigate()
  const location = useLocation()
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    // Bridge peut renvoyer un statut d'erreur (ex. authentification annulée).
    const status = (params.get('status') || params.get('step') || '').toLowerCase()
    const error = params.get('error')
    const failed = Boolean(error) || status === 'error' || status === 'failed'
    setHasError(failed)

    // On laisse au webhook quelques secondes pour importer, puis on bascule
    // vers Mes Comptes (succès) ou on revient en arrière (échec).
    const delay = failed ? 4000 : 3000
    const timer = setTimeout(() => {
      navigate('/MesComptes')
    }, delay)
    return () => clearTimeout(timer)
  }, [location.search, navigate])

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          pt: 15,
          pb: 30,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          color: 'white',
        }}
      >
        <Box sx={{ mb: 5 }}>
          <LifeLogo />
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          mt={4}
        >
          {hasError ? (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 56, color: '#ff6b6b', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                La connexion bancaire n'a pas abouti
              </Typography>
              <Typography variant="body2">
                Vous allez être redirigé vers vos comptes…
              </Typography>
            </>
          ) : (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#4caf50', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Banque connectée
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Vos comptes et opérations sont en cours d'importation…
              </Typography>
              <CircularProgress />
            </>
          )}
        </Box>
      </Box>
    </Container>
  )
}
